import {
  gtmDataLayerPush,
  UDP_TRACKING_EVENT_ADD_TO_CART,
  UDP_TRACKING_EVENT_CART_REMOVE,
  UDP_TRACKING_EVENT_CLICK,
  UDP_TRACKING_EVENT_IMPRESSION,
  UDP_TRACKING_EVENT_SEARCH,
} from '../gtm'

import {
  type UdpTrackingAddToCartEvent,
  type UdpTrackingCartRemoveEvent,
  type UdpTrackingClickEvent,
  type UdpTrackingImpressionEvent,
  type UdpTrackingImpressionOrigin,
  type UdpTrackingImpressionOriginData,
  type UdpTrackingMetadata,
  type UdpTrackingProductAddToCart,
  type UdpTrackingProductImpression,
  type UdpTrackingRecommendationAddToCartSource,
  type UdpTrackingSearchAttributionOrigin,
  type UdpTrackingSearchEvent,
  type UdpTrackingSearchFilters,
  type UdpTrackingSearchOrigin,
  type UdpTrackingSearchResult,
} from './model/udpTrackingTypes'
import { udpTrackingImpressionCacheGet } from './udpTrackingImpressionStorage'

export const udpTrackingDataLayerPushImpressions = ({
  impressions,
  metadata,
}: {
  impressions: UdpTrackingProductImpression[]
  metadata: UdpTrackingMetadata
}): void => {
  const event: UdpTrackingImpressionEvent = {
    event: UDP_TRACKING_EVENT_IMPRESSION,
    impressions: [...impressions],
    metadata,
  }

  gtmDataLayerPush(event)
}

export const udpTrackingDataLayerPushSearch = ({
  filters,
  metadata,
  page,
  query,
  queryId,
  results,
  searchOrigin,
  sort,
  totalResults,
}: {
  filters: UdpTrackingSearchFilters
  metadata: UdpTrackingMetadata
  page: number
  query: string
  queryId: string
  results: UdpTrackingSearchResult[]
  searchOrigin: UdpTrackingSearchOrigin
  sort: string
  totalResults: number
}): void => {
  const event: UdpTrackingSearchEvent = {
    event: UDP_TRACKING_EVENT_SEARCH,
    filters: { ...filters },
    metadata,
    page,
    query,
    query_id: queryId,
    results: [...results],
    search_origin: searchOrigin,
    sort,
    total_results: totalResults,
  }

  gtmDataLayerPush(event)
}

type UdpTrackingAddToCartParams = {
  addToCartSource?: UdpTrackingRecommendationAddToCartSource
  grossValue?: number
  impressionId: UdpTrackingAddToCartEvent['impression_id']
  impressionOrigin: UdpTrackingImpressionOrigin
  impressionOriginData: UdpTrackingImpressionOriginData
  metadata: UdpTrackingMetadata
  product: UdpTrackingProductAddToCart
}

export const udpTrackingDataLayerPushAddToCart = ({
  addToCartSource,
  grossValue,
  impressionId,
  impressionOrigin,
  impressionOriginData,
  metadata,
  product,
}: UdpTrackingAddToCartParams): void => {
  const event: UdpTrackingAddToCartEvent = {
    ...(addToCartSource && { a2c_source: addToCartSource }),
    event: UDP_TRACKING_EVENT_ADD_TO_CART,
    event_id: crypto.randomUUID(),
    ...(grossValue !== undefined && { gross_value: grossValue }),
    impression_id: impressionId,
    impression_origin: impressionOrigin,
    impression_origin_data: impressionOriginData,
    metadata,
    product,
  }

  gtmDataLayerPush(event)
}

export const udpTrackingDataLayerPushProductClick = ({
  impressionOrigin,
  metadata,
  upid,
  ...recommendationParams
}:
  | {
      impressionOrigin: 'recommendation'
      metadata: UdpTrackingMetadata
      upid: string
      widgetId: string
    }
  | {
      impressionOrigin: UdpTrackingSearchAttributionOrigin
      metadata: UdpTrackingMetadata
      upid: string
    }): void => {
  const widgetId =
    'widgetId' in recommendationParams
      ? recommendationParams.widgetId
      : undefined
  const impression = udpTrackingImpressionCacheGet(
    upid,
    impressionOrigin,
    widgetId,
  )

  if (impression?.impression_origin !== impressionOrigin) {
    return
  }

  const event: UdpTrackingClickEvent = {
    event: UDP_TRACKING_EVENT_CLICK,
    event_id: crypto.randomUUID(),
    impressions: [impression],
    metadata,
  }

  gtmDataLayerPush(event)
}

export const udpTrackingDataLayerPushCartRemove = ({
  metadata,
  ...attribution
}: Omit<UdpTrackingCartRemoveEvent, 'event_id' | 'event'>): void => {
  const event: UdpTrackingCartRemoveEvent = {
    ...attribution,
    event: UDP_TRACKING_EVENT_CART_REMOVE,
    event_id: crypto.randomUUID(),
    metadata,
  }

  gtmDataLayerPush(event)
}
